"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRecordsAll = void 0;
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const record_1 = require("../model/record");
const context_1 = require("../context");
const use_meta_1 = require("./use_meta");
const store_1 = require("../store");
/**
 * `Beta API`, possible future changes.
 *
 * Get all the records in the datasheet.
 * Rerendering is triggered when the value of record, view configuration, or field configuration changes.
 * Get all the records may cause lag due to a sharp increase in computation, so please use caution and test well.
 *
 * @returns
 *
 * ### Example
 * ```js
 * import { useRecordsAll, useDatasheet } from '@apitable/widget-sdk';
 *
 * // Show record title
 * function RecordsTitle() {
 *   const records = useRecordsAll();
 *   return (<div>
 *     {records.map(record => <p>{record.title}</p>)}
 *   </div>);
 * }
 *
 * // Show the primary key of records the corresponding to the datasheetId(dstXXXXXXXX) datasheet
 * function DatasheetRecordsTitle() {
 *   const useDatasheet = useDatasheet('dstXXXXXXXX');
 *   const records = useRecordsAll(useDatasheet);
 *   return (<div>
 *     {records.map(record => <p>{record.title}</p>)}
 *   </div>);
 * }
 * ```
 *
 */
function useRecordsAll(datasheet) {
    const context = (0, react_1.useContext)(context_1.WidgetContext);
    const { datasheetId: metaDatasheetId } = (0, use_meta_1.useMeta)();
    const datasheetId = datasheet ? datasheet.datasheetId : metaDatasheetId;
    const rows = (0, react_redux_1.useSelector)(state => {
        const datasheetItem = (0, store_1.getWidgetDatasheet)(state, datasheetId);
        return datasheetItem === null || datasheetItem === void 0 ? void 0 : datasheetItem.snapshot.meta.views[0].rows;
    });
    return (0, react_1.useMemo)(() => {
        if (!datasheetId || !rows)
            return [];
        return rows.map(row => new record_1.Record(datasheetId, context, row.recordId));
    }, [datasheetId, rows, context]);
}
exports.useRecordsAll = useRecordsAll;
